import { supabase } from './supabase';
import { scheduleNotification } from './notifications';

export async function checkTaskDeadlines() {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data: preferences } = await supabase
      .from('apro_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .maybeSingle();

    if (!preferences) return;

    const hoursAhead = preferences.deadline_reminder_hours || 24;
    const deadlineThreshold = new Date();
    deadlineThreshold.setHours(deadlineThreshold.getHours() + hoursAhead);

    const { data: tasks, error } = await supabase
      .from('apro_tasks')
      .select(`
        *,
        project:apro_projects (
          id,
          nombre
        )
      `)
      .eq('estado', 'en_marcha')
      .not('fecha_fin', 'is', null)
      .lte('fecha_fin', deadlineThreshold.toISOString())
      .gte('fecha_fin', new Date().toISOString());

    if (error || !tasks) return;

    for (const task of tasks) {
      const hoursRemaining = Math.round(
        (new Date(task.fecha_fin).getTime() - new Date().getTime()) / (1000 * 60 * 60)
      );

      if (hoursRemaining > 0) {
        const { data: existingNotifications } = await supabase
          .from('apro_notifications')
          .select('id')
          .eq('relacionado_id', task.id)
          .eq('relacionado_tipo', 'task')
          .eq('tipo', 'task_deadline')
          .gte('created_at', new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString());

        if (!existingNotifications || existingNotifications.length === 0) {
          await scheduleNotification(
            'task_deadline',
            'task_deadline',
            new Date(),
            {
              task_name: task.nombre,
              project_name: task.project?.nombre || 'Sin proyecto',
              hours_remaining: hoursRemaining,
              deadline: new Date(task.fecha_fin).toLocaleDateString('es-ES'),
              related_id: task.id,
              related_type: 'task',
            }
          );
        }
      }
    }
  } catch (error) {
    console.error('Error checking task deadlines:', error);
  }
}

export async function checkActiveTimers() {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;

    const { data: preferences } = await supabase
      .from('apro_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (!preferences) return;

    const minutesThreshold = preferences.timer_reminder_minutes || 30;
    const thresholdTime = new Date();
    thresholdTime.setMinutes(thresholdTime.getMinutes() - minutesThreshold);

    const { data: activeTasks, error } = await supabase
      .from('apro_tasks')
      .select('*')
      .eq('timer_activo', true)
      .lte('timer_inicio', thresholdTime.toISOString());

    if (error || !activeTasks) return;

    for (const task of activeTasks) {
      const minutesActive = Math.round(
        (new Date().getTime() - new Date(task.timer_inicio).getTime()) / (1000 * 60)
      );

      const { data: existingNotifications } = await supabase
        .from('apro_notifications')
        .select('id')
        .eq('relacionado_id', task.id)
        .eq('relacionado_tipo', 'task')
        .eq('tipo', 'timer_reminder')
        .gte('created_at', new Date(Date.now() - 30 * 60 * 1000).toISOString());

      if (!existingNotifications || existingNotifications.length === 0) {
        await scheduleNotification(
          'timer_reminder',
          'timer_reminder',
          new Date(),
          {
            task_name: task.nombre,
            minutes: minutesActive,
            related_id: task.id,
            related_type: 'task',
          }
        );
      }
    }
  } catch (error) {
    console.error('Error checking active timers:', error);
  }
}

let dailyReminderScheduledForUser: string | null = null;

export async function scheduleDailyReminder() {
  try {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) return;
    if (dailyReminderScheduledForUser === user.id) return;
    dailyReminderScheduledForUser = user.id;

    const { data: preferences } = await supabase
      .from('apro_notification_preferences')
      .select('*')
      .eq('user_id', user.id)
      .single();

    if (!preferences || !preferences.daily_reminder_time) return;

    const [hours, minutes] = preferences.daily_reminder_time.split(':');
    const reminderTime = new Date();
    reminderTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);

    if (reminderTime < new Date()) {
      reminderTime.setDate(reminderTime.getDate() + 1);
    }

    const { data: existingReminders, error: existingError } = await supabase
      .from('apro_notification_queue')
      .select('id')
      .eq('user_id', user.id)
      .eq('tipo', 'daily_reminder')
      .eq('status', 'pending')
      .limit(1);

    if (existingError) return;
    if (existingReminders && existingReminders.length > 0) return;

    const { data: pendingTasks, error } = await supabase
      .from('apro_tasks')
      .select('id')
      .in('estado', ['por_empezar', 'en_marcha', 'en_revision']);

    if (error) return;

    await scheduleNotification(
      'daily_reminder',
      'daily_reminder',
      reminderTime,
      {
        pending_tasks: pendingTasks?.length || 0,
      },
      true,
      'daily'
    );
  } catch (error) {
    console.error('Error scheduling daily reminder:', error);
  }
}

export async function notifyBudgetStatusChange(budgetId: string, newStatus: string) {
  try {
    const { data: budget, error } = await supabase
      .from('apro_budgets')
      .select(`
        *,
        client:apro_clients (
          nombre
        )
      `)
      .eq('id', budgetId)
      .single();

    if (error || !budget) return;

    const statusLabels: Record<string, string> = {
      por_enviar: 'Por Enviar',
      enviado: 'Enviado',
      rechazado: 'Rechazado',
      aceptado: 'Aceptado',
    };

    await scheduleNotification(
      'budget_status',
      'budget_status',
      new Date(),
      {
        budget_name: budget.nombre_proyecto || 'Sin nombre',
        status: statusLabels[newStatus] || newStatus,
        client_name: budget.client?.nombre || 'Sin cliente',
        related_id: budgetId,
        related_type: 'budget',
      }
    );
  } catch (error) {
    console.error('Error notifying budget status change:', error);
  }
}

export async function notifyProjectStatusChange(projectId: string, newStatus: string) {
  try {
    const { data: project, error } = await supabase
      .from('apro_projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (error || !project) return;

    const statusLabels: Record<string, string> = {
      propuesta: 'Propuesta',
      presupuestado: 'Presupuestado',
      aceptado: 'Aceptado',
      en_marcha: 'En Marcha',
      problema: 'Con Problemas',
      en_revision: 'En Revisión',
      terminado: 'Terminado',
      facturado: 'Facturado',
      cobrado: 'Cobrado',
    };

    await scheduleNotification(
      'project_update',
      'project_update',
      new Date(),
      {
        project_name: project.nombre,
        status: statusLabels[newStatus] || newStatus,
        related_id: projectId,
        related_type: 'project',
      }
    );
  } catch (error) {
    console.error('Error notifying project status change:', error);
  }
}

export function startNotificationMonitoring(): () => void {
  return () => {};
}
